/**
 * Runtime Configuration Service
 * Fetches configuration from the backend at application startup
 */

export interface AppConfig {
  serverUrl: string;
  stunServers: string[];
  turnServers: Array<{
    urls: string;
    username?: string;
    credential?: string;
  }>;
  livekitUrl?: string;
  environment: string;
  features: {
    googleAuth: boolean;
  };
}

class ConfigService {
  private config: AppConfig | null = null;
  private configPromise: Promise<AppConfig> | null = null;

  /**
   * Load configuration from the backend
   * Uses fallback values from build-time env vars if fetch fails
   */
  async loadConfig(): Promise<AppConfig> {
    // Return cached config if already loaded
    if (this.config) {
      return this.config;
    }

    // Return existing promise if already loading
    if (this.configPromise) {
      return this.configPromise;
    }

    // Start loading config
    this.configPromise = this.fetchConfig();
    this.config = await this.configPromise;
    return this.config;
  }

  private async fetchConfig(): Promise<AppConfig> {
    // SMART APPROACH: Use current origin as server URL to avoid Mixed Content issues
    // The client is served by the same server, so we can safely use window.location.origin
    const serverUrl = window.location.origin;
    
    try {
      // Try to get additional config from server (STUN/TURN servers, features)
      const response = await fetch('/api/config/client', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // Short timeout for faster fallback
        signal: AbortSignal.timeout(3000),
      });

      if (!response.ok) {
        throw new Error(`Config fetch failed: ${response.status}`);
      }

      const serverConfig = await response.json();
      
      // Use server config but override serverUrl with current origin
      const config = {
        ...serverConfig,
        serverUrl, // Always use current origin to prevent Mixed Content
      };
      
      console.log('✅ Runtime configuration loaded from server:', config);
      return config;

    } catch (error) {
      // Fallback to sensible defaults using current origin
      console.warn('⚠️  Failed to fetch runtime config, using current origin:', error);
      
      const config = {
        serverUrl, // Use current origin as fallback
        stunServers: [
          'stun:stun.l.google.com:19302',
          'stun:stun1.l.google.com:19302'
        ],
        turnServers: [], // No TURN servers by default
        environment: 'production',
        features: {
          googleAuth: true, // Assume available
        },
      };
      
      console.log('✅ Using fallback configuration:', config);
      return config;
    }
  }

  /**
   * Get the current configuration (must call loadConfig first)
   */
  getConfig(): AppConfig {
    if (!this.config) {
      throw new Error('Configuration not loaded. Call loadConfig() first.');
    }
    return this.config;
  }

  /**
   * Get server URL (convenience method)
   */
  getServerUrl(): string {
    return this.getConfig().serverUrl;
  }

  /**
   * Get ICE servers for WebRTC
   */
  getIceServers(): RTCIceServer[] {
    const config = this.getConfig();
    const iceServers: RTCIceServer[] = [];

    // Add STUN servers
    config.stunServers.forEach(url => {
      iceServers.push({ urls: url });
    });

    // Add TURN servers
    config.turnServers.forEach(turn => {
      iceServers.push({
        urls: turn.urls,
        username: turn.username,
        credential: turn.credential,
      });
    });

    return iceServers;
  }
}

// Export singleton instance
export const configService = new ConfigService();
export default configService;
